import {Component, Inject, ElementRef, ViewChild, AfterViewInit, OnDestroy} from '@angular/core';
import {MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {NgClass, NgIf, NgStyle, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common';
import { EventLoggerService } from '@services/event-logger-service';
import {SwiperModule} from 'swiper_angular';
import {SwiperOptions} from 'swiper';
import {CsIconModule} from '@components/icon';
import {Router} from '@angular/router';
import {CartService} from '@services/cart-service/cart.service';
import {extractAssetsForSlider} from '../../../../../../helper/product-helper';
import {ConnectionService} from '@services/connection-service';
import {AppConfig} from '../../../../../app.config';
import { ExperimentService } from '@services/experiment/experiment.service';

@Component({
  selector: 'nutra-push-bottom-sheet',
  templateUrl: './nutra-push-bottom-sheet.html',
  imports: [
    NgIf,
    NgClass,
    SwiperModule,
    CsIconModule,
    NgStyle,
    NgSwitchCase,
    NgSwitch,
    NgSwitchDefault,
  ],
  standalone: true,
})
export class NutraPushBottomSheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('descriptionSection') descriptionSection: ElementRef;
  @ViewChild('ingredientsSection') ingredientsSection: ElementRef;
  @ViewChild('howToUseSection') howToUseSection: ElementRef;
  @ViewChild('contentContainer') contentContainer: ElementRef;
  @ViewChild('tabDescription') tabDescription: ElementRef;
  @ViewChild('tabIngredients') tabIngredients: ElementRef;
  @ViewChild('tabHowToUse') tabHowToUse: ElementRef;

  activeTab: string = 'description';
  private scrollHandler: () => void;
  private isManualScroll: boolean = false;
  private manualScrollTimeout: any;
  nutraSliderAllImages: { type: "video" | "image"; url: string; }[];
  userLanguage: string;

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    public readonly eventLogger: EventLoggerService,
    public readonly appConfig: AppConfig,
    private readonly bottomSheetRef: MatBottomSheetRef<NutraPushBottomSheetComponent>,
    private readonly router: Router,
    private readonly cartService: CartService,
    private readonly conn: ConnectionService,
    private readonly experimentService: ExperimentService,
    ) {}

  swiperConfig: SwiperOptions = {
    slidesPerView: 1.75,
    autoHeight: true,
    pagination: false,
    navigation: false,
    autoplay: false,
  };

  ngOnInit() {
    this.nutraSliderAllImages = extractAssetsForSlider(this.data?.bottomSheetData);
    this.userLanguage = this.conn.getUserLanguage.toLowerCase();
  }

  ngAfterViewInit() {
    // Set up scroll handler
    this.scrollHandler = () => {
      if (!this.isManualScroll) {
        this.updateActiveTab();
      }
    };
    this.contentContainer.nativeElement.addEventListener('scroll', this.scrollHandler);
  }

  ngOnDestroy() {
    if (this.scrollHandler && this.contentContainer) {
      this.contentContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.manualScrollTimeout) {
      clearTimeout(this.manualScrollTimeout);
    }
  }

  private updateActiveTab() {
    const sections = [
      { id: 'description', element: this.descriptionSection?.nativeElement },
      { id: 'ingredients', element: this.ingredientsSection?.nativeElement },
      { id: 'howToUse', element: this.howToUseSection?.nativeElement }
    ];

    const bottomCTAcontainerHeight = 140;
    const containerRect = this.contentContainer.nativeElement.getBoundingClientRect();
    const containerTop = containerRect.top - bottomCTAcontainerHeight;
    const containerHeight = this.contentContainer.nativeElement.clientHeight;
    const minVisibleHeight = 145; // Minimum height of section that needs to be visible

    // Keep track of sections with minimum visible height
    let visibleSections = [];

    for (const section of sections) {
      if (!section.element) continue;

      const rect = section.element.getBoundingClientRect();

      // Calculate how much of the section is visible
      const elementTop = rect.top;
      const elementBottom = rect.bottom;
      const visibleTop = Math.max(elementTop, containerTop);
      const visibleBottom = Math.min(elementBottom, containerTop + containerHeight);
      const visibleHeight = visibleBottom - visibleTop;

      // Section is considered visible if at least minVisibleHeight pixels are showing
      if (visibleHeight >= minVisibleHeight) {
        visibleSections.push(section);
      }
    }

    // If we have visible sections, set the active tab to the last visible one
    if (visibleSections.length > 0) {
      this.activeTab = visibleSections[visibleSections.length - 1].id;
    }
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      // Set manual scroll flag to true
      this.isManualScroll = true;

      // Update active tab immediately
      this.activeTab = sectionId;

      this.contentContainer.nativeElement.scrollTo({
        top: element.offsetTop - 50,
        behavior: 'smooth'
      });

      // Clear any existing timeout
      if (this.manualScrollTimeout) {
        clearTimeout(this.manualScrollTimeout);
      }

      // Reset the manual scroll flag after the scroll animation completes
      this.manualScrollTimeout = setTimeout(() => {
        this.isManualScroll = false;
      }, 1000); // Adjust timeout to match scroll animation duration
    }
  }

  getIndicatorStyles(): { [key: string]: string } {
    let activeTabElement: ElementRef | null = null;

    // Get the active tab element reference
    switch (this.activeTab) {
      case 'description':
        activeTabElement = this.tabDescription;
        break;
      case 'ingredients':
        activeTabElement = this.tabIngredients;
        break;
      case 'howToUse':
        activeTabElement = this.tabHowToUse;
        break;
    }

    if (!activeTabElement?.nativeElement) {
      return { width: '0', transform: 'translateX(0)' };
    }

    const element = activeTabElement.nativeElement;
    const width = `${element.offsetWidth}px`;
    const left = element.offsetLeft;

    return {
      width,
      transform: `translateX(${left}px)`
    };
  }

  closeBottomSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  async buyNowHandler(): Promise<void> {
    const buyNowFlowExperiment = await this.isBuyNowFlowEnabled();
  
    // Add to cart if not already present in both cases
    await this.addToCartIfNotPresent();
  
    if (buyNowFlowExperiment) {
      await this.handleDirectCheckout();
    } else {
      await this.handleCartNavigation();
    }
  
    const eventData = {
      name: 'buy-now-cta-nutra-push-bottomsheet',
      currentCartItems: this.data?.cartItems,
      nutraId: this.data?.bottomSheetData?.objectId,
      buyNowFlowExperiment: buyNowFlowExperiment,
    };
    this.eventLogger.cleverTapEvent('click', JSON.stringify(eventData));
    this.closeBottomSheet();
  }

  private async isBuyNowFlowEnabled(): Promise<boolean> {
    try {
      const experiments = await this.conn.findUserActiveExperiments();
      return experiments?.some(exp => exp.key === 'buy_now_flow') || false;
    } catch (error) {
      return false;
    }
  }

  private async addToCartIfNotPresent(): Promise<void> {
    try {
      const productId = this.data?.bottomSheetData?.objectId;
      const cartItems = this.data?.cartItems || [];
      const isProductInCart = cartItems.some(item => item.productId === productId);

      if (!isProductInCart && productId) {
        const productsInCart = cartItems.reduce((acc, item) => {
          acc[item.productId] = item.quantity;
          return acc;
        }, {});

        await this.cartService.addOrRemoveFromCart(
          this.data?.bottomSheetData,
          'ADD',
          { productsInCart }
        );
      }
    } catch (error) {
      throw error;
    }
  }

  private async handleDirectCheckout(): Promise<void> {
    this.bottomSheetRef.dismiss();
    const user = this.conn.getActingUser();
    const orderState = user?.get('orderState');
    await this.router.navigate(['user/checkout'], {
      queryParams: {
        type: this.appConfig.Shared.Order.Type.PRODUCT,
        products: this.data?.bottomSheetData?.objectId,
        useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState))
      }
    });
  }

  private async handleCartNavigation(): Promise<void> {
    this.bottomSheetRef.dismiss();
    await this.router.navigate(['cart']);
  }

}
